import { FeatureFlag, type ResolverTag } from '@ps/web-core-enums';
import type { FetchTagPayload } from '@ps/web-core-store';
import { useIsMounted } from '@ps/web-shared-components';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import ENDPOINTS from '../constants/endpoints';

export type Options = Pick<FetchTagPayload, 'brand' | 'site'>;

const DEFAULT_TAG = '';

const useResolverV1 = (tag: ResolverTag): string => {
  const v1Enabled = useFeature(FeatureFlag.PKRMultiTableTournamentsResolverServiceV1);
  const brand = useSelector(select.cms.getBrandId);
  const site = useSelector(select.cms.getSiteId);
  const tagUrl =
    useSelector((rootState: RootState) => select.resolver.getUrl(rootState, tag)) || DEFAULT_TAG;
  const request = useRequest();
  const isMounted = useIsMounted();
  const {
    resolver: { response }
  } = useDispatch<Dispatch>();

  const fetchResolverTag = useCallback(
    async (tag: ResolverTag, options: Options) => {
      request<{ url: string }>({
        service: ENDPOINTS.resolveTagsV1,
        http: true,
        body: { name: tag, ...options }
      }).subscribe(
        ({ body }) => {
          if (isMounted()) response({ [tag]: body });
        },
        () => {
          //
        }
      );
    },
    [isMounted, request, response]
  );

  useEffect(() => {
    if (v1Enabled) {
      fetchResolverTag(tag, { site, brand });
    }
  }, [fetchResolverTag, tag, site, brand, v1Enabled]);

  return v1Enabled ? tagUrl : DEFAULT_TAG;
};

export default useResolverV1;
